import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, Search } from "lucide-react";

const maskPhone = (v) => v.replace(/\D/g, "").slice(0, 11).replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
const maskCpf = (v) => v.replace(/\D/g, "").slice(0, 11).replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const emailOk = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const emptyClient = { name: "", cpf: "", cep: "", phone: "", phone2: "", email: "", logradouro: "", bairro: "", cidade: "", estado: "", numero: "" };
const emptyDevice = { brand: "", model: "", color: "", imei: "" };

const OS_STATUS = {
  entrada: "Entrada", em_analise: "Em análise", sendo_reparado: "Sendo reparado",
  sem_conserto: "Sem conserto", reparado_aguardando: "Reparado (aguardando)",
  concluido_retirado: "Concluído e retirado",
};
const statusLabel = (k) => OS_STATUS[k] || String(k || "").replace(/_/g, " ");
const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; } };
const daysSince = (iso) => { try { return (Date.now() - new Date(iso).getTime()) / 86400000; } catch { return 9999; } };

export default function CreateOSModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [client, setClient] = useState(emptyClient);
  const [device, setDevice] = useState(emptyDevice);
  const [svc, setSvc] = useState({ reported_issue: "", performed_repair: "", repair_cost: "", charged_amount: "", requires_invoice: false });
  const [brands, setBrands] = useState([]);
  const [colors, setColors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [clientSummary, setClientSummary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setStep(1); setClient(emptyClient); setDevice(emptyDevice);
      setClientQuery(""); setClientResults([]); setClientId(null); setClientSummary(null);
      setSvc({ reported_issue: "", performed_repair: "", repair_cost: "", charged_amount: "", requires_invoice: false });
      api.get("/settings").then((r) => { setBrands(r.data?.os_brands || []); setColors(r.data?.os_colors || []); });
    }
  }, [open]);

  // Busca de cliente já cadastrado (autocompletar)
  useEffect(() => {
    if (!open || step !== 1) return;
    const q = clientQuery.trim();
    if (q.length < 2) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      setSearchingClients(true);
      try { const { data } = await api.get(`/clients?q=${encodeURIComponent(q)}`); setClientResults((data || []).slice(0, 8)); }
      catch { setClientResults([]); }
      finally { setSearchingClients(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [clientQuery, open, step]);

  const selectClient = async (c) => {
    setClientId(c.id);
    try {
      const { data } = await api.get(`/clients/${c.id}`);
      const f = data.client || data;
      setClient({
        name: f.name || "", cpf: f.cpf || f.document || "", cep: f.cep || "",
        phone: f.phone || "", phone2: f.phone2 || "", email: f.email || "",
        logradouro: f.logradouro || f.address || "", bairro: f.bairro || f.neighborhood || "",
        cidade: f.cidade || f.city || "", estado: f.estado || "", numero: f.numero || f.number || "",
      });
      const orders = data.service_orders || [];
      const devSales = data.device_sales || [];
      const inWarranty = orders.some((o) => o.status === "concluido_retirado" && daysSince(o.updatedAt || o.createdAt) <= 90);
      setClientSummary({ orders: orders.slice(0, 3), ordersCount: orders.length, devSalesCount: devSales.length, inWarranty });
    } catch {
      setClient((prev) => ({ ...prev, name: c.name, phone: c.phone || "", phone2: c.phone2 || "", cpf: c.document || "" }));
      setClientSummary(null);
    }
    setClientQuery(""); setClientResults([]);
    toast.success(`Cliente ${c.name} selecionado`);
  };

  const lookupCep = async (cep) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const d = await r.json();
      if (!d.erro) setClient((c) => ({ ...c, logradouro: d.logradouro || "", bairro: d.bairro || "", cidade: d.localidade || "", estado: d.uf || "" }));
    } catch (e) { console.error("Falha ao consultar CEP", e); } finally { setCepLoading(false); }
  };

  const nextStep = () => {
    if (!client.name.trim()) { toast.error("Nome do cliente é obrigatório."); return; }
    if (!emailOk(client.email)) { toast.error("E-mail inválido."); return; }
    setStep(2);
  };

  const submit = async () => {
    if (!device.brand) { toast.error("Selecione a marca."); return; }
    if (!device.model.trim()) { toast.error("Informe o modelo."); return; }
    if (!svc.reported_issue.trim()) { toast.error("Descreva o defeito relatado."); return; }
    if (svc.charged_amount === "" || isNaN(parseFloat(svc.charged_amount))) { toast.error("Valor cobrado é obrigatório."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/service-orders", {
        client, client_id: clientId, device,
        reported_issue: svc.reported_issue, performed_repair: svc.performed_repair,
        repair_cost: parseFloat(svc.repair_cost) || 0, charged_amount: parseFloat(svc.charged_amount),
        requires_invoice: svc.requires_invoice,
      });
      toast.success(`${data.number} criada — retirada ${data.withdrawalCode}`);
      onClose(); navigate(`/app/os/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="create-os-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Nova Ordem de Serviço · Etapa {step}/2</DialogTitle>
          <DialogDescription className="text-zinc-500">{step === 1 ? "Dados do cliente (nome obrigatório)" : "Dados do aparelho e valores"}</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="relative">
            <F label="Buscar cliente já cadastrado">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input data-testid="os-client-search" value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} className="i pl-9" placeholder="Nome, telefone ou CPF..." />
                {searchingClients && <Loader2 size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500" />}
              </div>
            </F>
            {clientResults.length > 0 && (
              <div className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-gold/20 bg-[#0E0E12] shadow-xl max-h-56 overflow-y-auto" data-testid="os-client-results">
                {clientResults.map((c) => (
                  <button key={c.id} type="button" data-testid={`os-client-opt-${c.id}`} onClick={() => selectClient(c)} className="w-full text-left px-3 py-2 hover:bg-gold/10 border-b border-white/5 last:border-0">
                    <div className="text-sm text-zinc-100">{c.name}</div>
                    <div className="text-xs text-zinc-500">{[c.phone, c.document].filter(Boolean).join(" · ") || "sem telefone"}</div>
                  </button>
                ))}
              </div>
            )}
            {clientId && (
              <div className="mt-2 space-y-2" data-testid="os-client-linked">
                <div className="flex items-center justify-between text-xs bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                  <span className="text-green-300">✓ Vinculado ao cadastro — não será duplicado</span>
                  <button type="button" data-testid="os-client-unlink" onClick={() => { setClientId(null); setClientSummary(null); setClient(emptyClient); }} className="text-zinc-400 hover:text-white underline">Novo cliente</button>
                </div>
                {clientSummary && (
                  <div className="rounded-lg border border-gold/15 bg-[#0E0E12] px-3 py-2.5" data-testid="os-client-summary">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-xs font-semibold text-zinc-200">Resumo do cliente</span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/15 text-gold-bright">{clientSummary.ordersCount} OS</span>
                      {clientSummary.devSalesCount > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-zinc-300">{clientSummary.devSalesCount} compra(s)</span>}
                      {clientSummary.inWarranty && <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-300 border border-green-500/30" data-testid="os-client-warranty">Em garantia (90 dias)</span>}
                    </div>
                    {clientSummary.orders.length === 0 ? (
                      <p className="text-[11px] text-zinc-500">Primeira OS deste cliente.</p>
                    ) : (
                      <ul className="space-y-1">
                        {clientSummary.orders.map((o) => (
                          <li key={o.id} className="text-[11px] text-zinc-400 flex items-center justify-between gap-2">
                            <span className="text-zinc-300">{o.number}</span>
                            <span className="truncate">{statusLabel(o.status)}</span>
                            <span className="text-zinc-500">{fmtD(o.createdAt)} · {money(o.chargedAmount)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 1 ? (
          <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="sm:col-span-2"><F label="Nome completo *"><Input data-testid="os-client-name" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} className="i" /></F></div>
            <F label="CPF"><Input data-testid="os-client-cpf" value={client.cpf} onChange={(e) => setClient({ ...client, cpf: maskCpf(e.target.value) })} className="i" placeholder="000.000.000-00" /></F>
            <F label="Telefone (WhatsApp)"><Input data-testid="os-client-phone" value={client.phone} onChange={(e) => setClient({ ...client, phone: maskPhone(e.target.value) })} className="i" placeholder="(61) 99999-0000" /></F>
            <F label="Telefone de recado"><Input data-testid="os-client-phone2" value={client.phone2} onChange={(e) => setClient({ ...client, phone2: maskPhone(e.target.value) })} className="i" placeholder="opcional" /></F>
            <F label="CEP"><Input data-testid="os-client-cep" value={client.cep} onChange={(e) => { setClient({ ...client, cep: e.target.value }); lookupCep(e.target.value); }} className="i" placeholder="00000-000" /></F>
            <F label={cepLoading ? "Cidade (buscando...)" : "Cidade"}><Input value={client.cidade} onChange={(e) => setClient({ ...client, cidade: e.target.value })} className="i" /></F>
            <div className="sm:col-span-2"><F label="Logradouro"><Input value={client.logradouro} onChange={(e) => setClient({ ...client, logradouro: e.target.value })} className="i" /></F></div>
            <F label="Bairro"><Input value={client.bairro} onChange={(e) => setClient({ ...client, bairro: e.target.value })} className="i" /></F>
            <F label="Estado"><Input value={client.estado} onChange={(e) => setClient({ ...client, estado: e.target.value })} className="i" /></F>
            <div className="sm:col-span-2"><F label="E-mail"><Input data-testid="os-client-email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} className="i" /></F></div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
            <F label="Marca *">
              <Select value={device.brand} onValueChange={(v) => setDevice({ ...device, brand: v })}>
                <SelectTrigger data-testid="os-device-brand" className="i"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-[#121217] border-gold/20 text-white">
                  {brands.map((b) => <SelectItem key={b} value={b} className="focus:bg-gold/10">{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Modelo *"><Input data-testid="os-device-model" value={device.model} onChange={(e) => setDevice({ ...device, model: e.target.value })} className="i" placeholder="iPhone 14 Pro" /></F>
            <F label="Cor">
              <Select value={device.color} onValueChange={(v) => setDevice({ ...device, color: v })}>
                <SelectTrigger data-testid="os-device-color" className="i"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-[#121217] border-gold/20 text-white">
                  {colors.map((c) => <SelectItem key={c} value={c} className="focus:bg-gold/10">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="IMEI (15 dígitos)"><Input value={device.imei} onChange={(e) => setDevice({ ...device, imei: e.target.value.replace(/\D/g, "").slice(0, 15) })} className="i" /></F>
            <div className="sm:col-span-2"><F label="Defeito relatado *"><Textarea data-testid="os-issue" value={svc.reported_issue} onChange={(e) => setSvc({ ...svc, reported_issue: e.target.value })} className="i" rows={2} /></F></div>
            <div className="sm:col-span-2"><F label="Reparo efetuado (opcional)"><Textarea value={svc.performed_repair} onChange={(e) => setSvc({ ...svc, performed_repair: e.target.value })} className="i" rows={2} /></F></div>
            <F label="Custo do reparo (R$)"><Input type="number" value={svc.repair_cost} onChange={(e) => setSvc({ ...svc, repair_cost: e.target.value })} className="i" /></F>
            <F label="Valor cobrado (R$) *"><Input data-testid="os-charged" type="number" value={svc.charged_amount} onChange={(e) => setSvc({ ...svc, charged_amount: e.target.value })} className="i" /></F>
            <label className="sm:col-span-2 flex items-center gap-3 text-sm text-zinc-300">
              <Checkbox checked={svc.requires_invoice} onCheckedChange={(v) => setSvc({ ...svc, requires_invoice: !!v })} className="border-gold/40 data-[state=checked]:bg-gold data-[state=checked]:text-black" data-testid="os-invoice" />
              Emitir nota fiscal (requiresInvoice)
            </label>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && <Button variant="ghost" className="text-zinc-400 mr-auto" onClick={() => setStep(1)}><ArrowLeft size={16} className="mr-1" />Voltar</Button>}
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          {step === 1 ? (
            <Button data-testid="os-next" onClick={nextStep} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Próximo <ArrowRight size={16} className="ml-1" /></Button>
          ) : (
            <Button data-testid="os-submit" onClick={submit} disabled={saving} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>{saving ? <Loader2 className="animate-spin" size={16} /> : "Criar OS"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (<div><Label className="text-xs text-zinc-400 mb-1.5 block">{label}</Label>{children}</div>);
